// AssigneesModule — team-member assignments on a job.
//
// Renders inside DetailDrawer when the record has a jobId. Shows current
// assignees (avatar + name + role + ×) and an "Add assignee" picker that
// shows tenant team members not yet assigned. Talks to:
//   GET    /api/jobs/:jobId/assignments
//   POST   /api/jobs/:jobId/assignments
//   DELETE /api/jobs/:jobId/assignments/:userId
//   GET    /api/users/workspace-members

import { useCallback, useEffect, useMemo, useState } from 'react'
import { UserPlus, Users, X } from 'lucide-react'
import { apiClient } from '@/lib/api'
import { toast } from '@/lib/toast'
import { ErrorState } from '@/components/ui/QueryState'
import { Button } from '@/components/ui/Button'
import { SectionHeader, ModulePanel, GhostButton } from './_drawerHelpers'
import { getInitials } from '@/utils/formatting'

type Role = 'LEAD' | 'MEMBER' | 'OBSERVER'

interface Member {
  id: string
  firstName: string
  lastName: string
  email: string
  role: string
}

interface Assignment {
  id: string
  userId: string
  role: Role
  user: Member
}

interface Props {
  jobId: string
  onChanged?: () => void
  /** Show who is on the job without add/remove (people who can't edit jobs). */
  readOnly?: boolean
}

function initialsOf(m: { firstName: string; lastName: string; email: string }): string {
  // Shared helper: skips "[DEMO]"-style prefixes and non-letters.
  const name = `${m.firstName ?? ''} ${m.lastName ?? ''}`.trim()
  return name ? getInitials(name) : m.email.slice(0, 2).toUpperCase()
}

function fullName(m: { firstName: string; lastName: string; email: string }): string {
  const name = `${m.firstName ?? ''} ${m.lastName ?? ''}`.trim()
  return name || m.email
}

export function AssigneesModule({ jobId, onChanged, readOnly = false }: Props) {
  const [assignments, setAssignments] = useState<Assignment[]>([])
  const [members, setMembers] = useState<Member[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<unknown>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [mutating, setMutating] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    try {
      // The member list is only for the picker (and SALES+ only), so a
      // read-only view asks for the assignments alone.
      const [{ data: aRes }, mRes] = await Promise.all([
        apiClient.get<{ data: { items: Assignment[] } }>(`/api/jobs/${jobId}/assignments`),
        readOnly ? null : apiClient.get<{ data: { items: Member[] } }>(`/api/users/workspace-members`),
      ])
      setAssignments(aRes?.data?.items ?? [])
      setMembers(mRes?.data?.data?.items ?? [])
    } catch (err) {
      setLoadError(err)
    } finally {
      setLoading(false)
    }
  }, [jobId, readOnly])

  useEffect(() => {
    void load()
  }, [load])

  const availableMembers = useMemo(() => {
    const assignedIds = new Set(assignments.map((a) => a.userId))
    return members.filter((m) => !assignedIds.has(m.id))
  }, [assignments, members])

  const handleAdd = async (userId: string) => {
    if (mutating) return
    setMutating(true)
    try {
      await apiClient.post(`/api/jobs/${jobId}/assignments`, { userId, role: 'MEMBER' })
      const added = members.find((m) => m.id === userId)
      toast.success(added ? `${fullName(added)} added to the crew` : 'Added to the crew')
      setPickerOpen(false)
      await load()
      onChanged?.()
    } catch (err) {
      toast.error(err, 'add them to the crew')
    } finally {
      setMutating(false)
    }
  }

  const handleRemove = async (userId: string) => {
    if (mutating) return
    setMutating(true)
    try {
      await apiClient.delete(`/api/jobs/${jobId}/assignments/${userId}`)
      const removed = assignments.find((a) => a.userId === userId)
      toast.success(removed ? `${fullName(removed.user)} removed from the crew` : 'Removed from the crew')
      await load()
      onChanged?.()
    } catch (err) {
      toast.error(err, 'remove them from the crew')
    } finally {
      setMutating(false)
    }
  }

  return (
    <ModulePanel accent="neutral">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <SectionHeader
          tone="neutral"
          icon={Users}
          title="Assignees"
          meta={assignments.length > 0 ? <span>{assignments.length}</span> : undefined}
        />
      </div>

      {/* Current assignees list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {loading && (
          <p role="status" style={{ fontSize: 13, color: 'var(--midnight-text3)' }}>Loading the crew…</p>
        )}

        {!loading && loadError != null && (
          <ErrorState compact error={loadError} action="load the crew" forbiddenWhat="see the crew" onRetry={() => { void load() }} />
        )}

        {!loading && loadError == null && assignments.length === 0 && (
          <p style={{ fontSize: 13, color: 'var(--midnight-text3)' }}>
            No one assigned yet.
          </p>
        )}

        {assignments.map((a) => (
          <div
            key={a.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 10px',
              borderRadius: 12,
              background: 'rgb(var(--bg-rgb) / 0.25)',
              border: '0.5px solid rgb(var(--fg-rgb) / 0.06)',
            }}
          >
            <span
              aria-hidden="true"
              title={fullName(a.user)}
              style={{
                width: 28,
                height: 28,
                borderRadius: 9999,
                background: 'rgb(var(--fg-rgb) / 0.06)',
                border: '0.5px solid rgb(var(--fg-rgb) / 0.12)',
                fontSize: 12,
                fontWeight: 600,
                color: 'rgb(var(--fg-rgb) / 0.70)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {initialsOf(a.user)}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p
                style={{
                  fontSize: 14,
                  color: 'rgb(var(--fg-rgb))',
                  margin: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {fullName(a.user)}
              </p>
              <p
                style={{
                  fontSize: 12,
                  color: 'var(--midnight-text3)',
                  margin: 0,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                }}
              >
                {a.role}
              </p>
            </div>
            {!readOnly && <button
              type="button"
              onClick={() => handleRemove(a.userId)}
              disabled={mutating}
              aria-label={`Remove ${fullName(a.user)}`}
              style={{
                width: 28,
                height: 28,
                borderRadius: 9999,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgb(var(--fg-rgb) / 0.04)',
                border: '0.5px solid rgb(var(--fg-rgb) / 0.08)',
                color: 'var(--midnight-text3)',
                cursor: mutating ? 'wait' : 'pointer',
                flexShrink: 0,
              }}
            >
              <X style={{ width: 12, height: 12 }} />
            </button>}
          </div>
        ))}
      </div>

      {/* Add assignee */}
      {!readOnly && <div style={{ marginTop: 12 }}>
        {!pickerOpen && availableMembers.length > 0 && (
          <GhostButton
            icon={UserPlus}
            label="Add assignee"
            onClick={() => setPickerOpen(true)}
          />
        )}

        {pickerOpen && (
          <div
            style={{
              marginTop: 8,
              padding: 8,
              borderRadius: 12,
              background: 'rgb(var(--bg-rgb) / 0.30)',
              border: '0.5px solid rgb(var(--fg-rgb) / 0.08)',
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}
          >
            {availableMembers.length === 0 && (
              <p style={{ fontSize: 13, color: 'var(--midnight-text3)', padding: '8px 10px' }}>
                Everyone in your workspace is already assigned.
              </p>
            )}
            {availableMembers.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => handleAdd(m.id)}
                disabled={mutating}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '8px 10px',
                  borderRadius: 10,
                  background: 'transparent',
                  border: 'none',
                  color: 'inherit',
                  cursor: mutating ? 'wait' : 'pointer',
                  textAlign: 'left',
                  width: '100%',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgb(var(--fg-rgb) / 0.04)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent'
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 9999,
                    background: 'rgb(var(--fg-rgb) / 0.06)',
                    border: '0.5px solid rgb(var(--fg-rgb) / 0.12)',
                    fontSize: 12,
                    fontWeight: 600,
                    color: 'rgb(var(--fg-rgb) / 0.70)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {initialsOf(m)}
                </span>
                <span
                  style={{
                    fontSize: 14,
                    color: 'rgb(var(--fg-rgb))',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {fullName(m)}
                </span>
              </button>
            ))}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPickerOpen(false)}
              fullWidth
            >
              Cancel
            </Button>
          </div>
        )}
      </div>}
    </ModulePanel>
  )
}
